import { useState, useRef } from 'react';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useFormSubmit } from '../../../hooks/useFormSubmit';
import { useToast } from '../../../components/base/Toast';
import { getSupabase } from '../../../lib/supabase';
import { useVinLookup } from '../../../hooks/useVinLookup';

const SellCarSchema = z.object({
  name: z.string().min(2, 'Please enter your full name'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().min(7, 'Please enter a valid phone number'),
  year: z.string().min(4, 'Please enter a valid year').max(4, 'Please enter a valid year'),
  make: z.string().min(1, 'Please enter the vehicle make'),
  model: z.string().min(1, 'Please enter the vehicle model'),
  trim: z.string().max(50, 'Trim cannot exceed 50 characters').optional(),
  mileage: z.string().min(1, 'Please enter the mileage'),
  condition: z.string().min(1, 'Please select a condition'),
  message: z.string().max(500, 'Message cannot exceed 500 characters').optional(),
});

function getLeadSource(): Record<string, string> {
  const params = new URLSearchParams(window.location.search);
  const stored = (() => {
    try { return JSON.parse(sessionStorage.getItem('rha_lead_source') || '{}'); } catch { return {}; }
  })();
  const source: Record<string, string> = {
    utm_source: params.get('utm_source') || stored.utm_source || '',
    utm_medium: params.get('utm_medium') || stored.utm_medium || '',
    utm_campaign: params.get('utm_campaign') || stored.utm_campaign || '',
    utm_term: params.get('utm_term') || stored.utm_term || '',
    utm_content: params.get('utm_content') || stored.utm_content || '',
    gclid: params.get('gclid') || stored.gclid || '',
    referrer: stored.referrer || document.referrer || '',
    landing_page: stored.landing_page || window.location.pathname,
  };
  if (!stored.utm_source && (source.utm_source || source.gclid)) {
    sessionStorage.setItem('rha_lead_source', JSON.stringify(source));
  }
  return source;
}

export default function SellCarForm() {
  const navigate = useNavigate();
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [submitError, setSubmitError] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    year: '',
    make: '',
    model: '',
    trim: '',
    mileage: '',
    condition: '',
    message: '',
  });

  const vinLookup = useVinLookup(
    ({ year, make, model, trim }) => {
      setFormData(prev => ({ ...prev, year, make, model, trim }));
    },
    () => {
      setFormData(prev => ({ ...prev, year: '', make: '', model: '', trim: '' }));
    },
  );

  const { handleSubmit, isSubmitting, validationErrors } = useFormSubmit({
    url: 'https://readdy.ai/api/form/d6j89mnrgrhbthj8mqdg',
    schema: SellCarSchema,
    formRef,
    onSuccess: () => {
      setSubmitError(false);
      const params = new URLSearchParams({
        year: formData.year,
        make: formData.make,
        model: formData.model,
        from: 'sell',
      });
      navigate(`/thank-you/sell-your-car?${params.toString()}`);
    },
    onError: () => {
      setSubmitError(true);
      toast.error('Submission failed', 'Something went wrong. Please try again or call us directly.');
    },
    conversionLabel: 'SELL_CAR_LEAD',
    conversionValue: 30.0,
    leadEventName: 'Sell Your Car Form',
  });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(false);
    const leadSource = getLeadSource();

    try {
      const supabase = await getSupabase();
      const { data: leadData, error: leadError } = await supabase
        .from('leads')
        .insert({
          lead_type: 'sell_your_car',
          lead_source: leadSource.utm_source || leadSource.referrer || 'direct',
          lead_medium: leadSource.utm_medium || null,
          lead_campaign: leadSource.utm_campaign || null,
          lead_status: 'new',
          utm_source: leadSource.utm_source || null,
          utm_medium: leadSource.utm_medium || null,
          utm_campaign: leadSource.utm_campaign || null,
          utm_term: leadSource.utm_term || null,
          utm_content: leadSource.utm_content || null,
          referrer_url: leadSource.referrer || null,
          landing_page: leadSource.landing_page || window.location.pathname,
          user_agent: navigator.userAgent,
          form_data: {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            vin: vinLookup.vin.trim().toUpperCase() || null,
            year: formData.year,
            make: formData.make,
            model: formData.model,
            trim: formData.trim || vinLookup.details?.trim || null,
            engine: vinLookup.details?.engine || null,
            drivetrain: vinLookup.details?.drivetrain || null,
            body_style: vinLookup.details?.bodyStyle || null,
            mileage: formData.mileage,
            condition: formData.condition,
            message: formData.message,
            form_name: 'Sell Your Car',
            page_url: window.location.href,
            submitted_at: new Date().toISOString(),
          },
        })
        .select('id')
        .maybeSingle();

      if (leadError) {
        console.error('Supabase insert error:', leadError);
      } else if (leadData?.id) {
        sessionStorage.setItem('rha_last_lead_id', leadData.id);
      }
    } catch (supabaseError) {
      console.error('Supabase connection error:', supabaseError);
    }

    await handleSubmit({
      ...formData,
      vin: vinLookup.vin.trim().toUpperCase(),
      ...leadSource,
      form_name: 'Sell Your Car',
      page_url: window.location.href,
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const fieldClass = (field: string) =>
    `w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1A2332] focus:border-transparent transition-all text-sm ${
      validationErrors[field] ? 'border-red-400 bg-red-50' : 'border-[#E8E2D6]'
    }`;

  return (
    <div id="form" className="bg-white rounded-2xl border border-[#E8E2D6] p-8 shadow-sm">
      <div role="status" aria-live="assertive" aria-atomic="true" className="sr-only">
        {isSubmitting && 'Submitting your cash offer request, please wait.'}
        {submitError && 'Submission failed. Please try again or call us directly.'}
      </div>

      <h2 className="font-['Oswald'] text-3xl font-semibold text-[#1A2332] mb-2">Get Your Cash Offer</h2>
      <p className="text-[#4A5568] mb-8 text-sm">Fill out the form below and we'll provide you with a competitive cash offer for your vehicle.</p>

      <form id="sell-car-form" onSubmit={onSubmit} className="space-y-6" data-readdy-form ref={formRef}>

        {/* ── VIN Lookup ── */}
        <div className="bg-[#F7F5F0] border border-[#E8E2D6] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-barcode-line text-[#E8B84B] text-lg" aria-hidden="true"></i>
            </div>
            <span className="text-sm font-semibold text-[#1A2332]">VIN Lookup — Auto-Fill Vehicle Details</span>
            <span className="ml-auto text-xs text-[#4A5568] bg-white border border-[#E8E2D6] rounded-full px-2 py-0.5">Optional</span>
          </div>
          <p className="text-xs text-[#4A5568] mb-3 pl-7">Enter your 17-character VIN to instantly fill in year, make &amp; model.</p>

          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                id="vin"
                name="vin"
                value={vinLookup.vin}
                onChange={e => vinLookup.setVin(e.target.value)}
                onKeyDown={vinLookup.onKeyDown}
                maxLength={17}
                placeholder="e.g. 1HGBH41JXMN109186"
                aria-label="Vehicle Identification Number"
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1A2332] focus:border-transparent transition-all text-sm font-mono tracking-widest uppercase pr-10 ${
                  vinLookup.status === 'error' ? 'border-red-400 bg-red-50' :
                  vinLookup.status === 'success' ? 'border-green-400 bg-green-50' :
                  'border-[#E8E2D6] bg-white'
                }`}
              />
              {vinLookup.vin.length > 0 && (
                <button
                  type="button"
                  onClick={vinLookup.clear}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A5568] hover:text-[#1A2332] cursor-pointer"
                  aria-label="Clear VIN"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-close-line text-sm" aria-hidden="true"></i>
                  </div>
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={vinLookup.lookup}
              disabled={vinLookup.status === 'loading' || vinLookup.vin.length === 0}
              className="px-5 py-3 bg-[#1A2332] text-white rounded-xl text-sm font-semibold hover:bg-[#2C3E55] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              {vinLookup.status === 'loading' ? (
                <>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin text-sm" aria-hidden="true"></i>
                  </div>
                  Looking up...
                </>
              ) : (
                <>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-search-line text-sm" aria-hidden="true"></i>
                  </div>
                  Decode VIN
                </>
              )}
            </button>
          </div>

          <div className="mt-1 flex items-center gap-1.5 min-h-[20px]">
            <span className="text-xs text-[#4A5568]">{vinLookup.vin.length}/17 characters</span>
            {vinLookup.status === 'success' && (
              <span className="ml-auto flex items-center gap-1 text-xs text-green-700 font-medium">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-checkbox-circle-fill text-green-600" aria-hidden="true"></i>
                </div>
                {vinLookup.message}
              </span>
            )}
            {vinLookup.status === 'error' && (
              <span className="ml-auto flex items-center gap-1 text-xs text-red-600">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-error-warning-line" aria-hidden="true"></i>
                </div>
                {vinLookup.message}
              </span>
            )}
          </div>

          {/* Extra decoded details pill row */}
          {vinLookup.status === 'success' && vinLookup.details && (
            <div className="mt-3 flex flex-wrap gap-2">
              {vinLookup.details.trim && (
                <span className="inline-flex items-center gap-1 bg-white border border-[#E8E2D6] rounded-full px-3 py-1 text-xs text-[#1A2332]">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-price-tag-3-line text-[#E8B84B]" aria-hidden="true"></i>
                  </div>
                  Trim: {vinLookup.details.trim}
                </span>
              )}
              {vinLookup.details.engine && (
                <span className="inline-flex items-center gap-1 bg-white border border-[#E8E2D6] rounded-full px-3 py-1 text-xs text-[#1A2332]">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-settings-3-line text-[#E8B84B]" aria-hidden="true"></i>
                  </div>
                  Engine: {vinLookup.details.engine}
                </span>
              )}
              {vinLookup.details.drivetrain && (
                <span className="inline-flex items-center gap-1 bg-white border border-[#E8E2D6] rounded-full px-3 py-1 text-xs text-[#1A2332]">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-steering-2-line text-[#E8B84B]" aria-hidden="true"></i>
                  </div>
                  {vinLookup.details.drivetrain}
                </span>
              )}
              {vinLookup.details.bodyStyle && (
                <span className="inline-flex items-center gap-1 bg-white border border-[#E8E2D6] rounded-full px-3 py-1 text-xs text-[#1A2332]">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-car-line text-[#E8B84B]" aria-hidden="true"></i>
                  </div>
                  {vinLookup.details.bodyStyle}
                </span>
              )}
            </div>
          )}
        </div>

        {/* ── Contact Info ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="name" className="flex items-center gap-2 text-sm font-medium text-[#1A2332] mb-2">
              <i className="ri-user-line text-[#E8B84B]" aria-hidden="true"></i>Full Name
            </label>
            <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required aria-required="true" aria-invalid={!!validationErrors.name} aria-describedby={validationErrors.name ? 'sell-name-error' : undefined} className={fieldClass('name')} placeholder="John Doe" autoComplete="name" />
            {validationErrors.name && <p id="sell-name-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.name}</p>}
          </div>
          <div>
            <label htmlFor="email" className="flex items-center gap-2 text-sm font-medium text-[#1A2332] mb-2">
              <i className="ri-mail-line text-[#E8B84B]" aria-hidden="true"></i>Email Address
            </label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required aria-required="true" aria-invalid={!!validationErrors.email} aria-describedby={validationErrors.email ? 'sell-email-error' : undefined} className={fieldClass('email')} placeholder="john@example.com" autoComplete="email" />
            {validationErrors.email && <p id="sell-email-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.email}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="phone" className="flex items-center gap-2 text-sm font-medium text-[#1A2332] mb-2">
            <i className="ri-phone-line text-[#E8B84B]" aria-hidden="true"></i>Phone Number
          </label>
          <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required aria-required="true" aria-invalid={!!validationErrors.phone} aria-describedby={validationErrors.phone ? 'sell-phone-error' : undefined} className={fieldClass('phone')} placeholder="(555) 123-4567" autoComplete="tel" />
          {validationErrors.phone && <p id="sell-phone-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.phone}</p>}
        </div>

        {/* ── Vehicle Info ── */}
        <div className="border-t border-[#E8E2D6] pt-6">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="font-['Oswald'] text-xl font-semibold text-[#1A2332]">Vehicle Information</h3>
            {vinLookup.status === 'success' && (
              <span className="flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-magic-line text-green-600 text-xs" aria-hidden="true"></i>
                </div>
                Auto-filled from VIN
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <label htmlFor="year" className="flex items-center gap-2 text-sm font-medium text-[#1A2332] mb-2">
                <i className="ri-calendar-line text-[#E8B84B]" aria-hidden="true"></i>Year
              </label>
              <input type="text" id="year" name="year" value={formData.year} onChange={handleChange} required aria-required="true" aria-invalid={!!validationErrors.year} aria-describedby={validationErrors.year ? 'sell-year-error' : undefined} className={fieldClass('year')} placeholder="2020" />
              {validationErrors.year && <p id="sell-year-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.year}</p>}
            </div>
            <div>
              <label htmlFor="make" className="flex items-center gap-2 text-sm font-medium text-[#1A2332] mb-2">
                <i className="ri-car-line text-[#E8B84B]" aria-hidden="true"></i>Make
              </label>
              <input type="text" id="make" name="make" value={formData.make} onChange={handleChange} required aria-required="true" aria-invalid={!!validationErrors.make} aria-describedby={validationErrors.make ? 'sell-make-error' : undefined} className={fieldClass('make')} placeholder="Toyota" />
              {validationErrors.make && <p id="sell-make-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.make}</p>}
            </div>
            <div>
              <label htmlFor="model" className="flex items-center gap-2 text-sm font-medium text-[#1A2332] mb-2">
                <i className="ri-roadster-line text-[#E8B84B]" aria-hidden="true"></i>Model
              </label>
              <input type="text" id="model" name="model" value={formData.model} onChange={handleChange} required aria-required="true" aria-invalid={!!validationErrors.model} aria-describedby={validationErrors.model ? 'sell-model-error' : undefined} className={fieldClass('model')} placeholder="Camry" />
              {validationErrors.model && <p id="sell-model-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.model}</p>}
            </div>
          </div>

          {/* ── Trim + Mileage + Condition row ── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label htmlFor="trim" className="flex items-center gap-2 text-sm font-medium text-[#1A2332] mb-2">
                <i className="ri-price-tag-3-line text-[#E8B84B]" aria-hidden="true"></i>
                Trim / Sub-model
                <span className="ml-auto text-xs text-[#4A5568] bg-[#F7F5F0] border border-[#E8E2D6] rounded-full px-2 py-0.5">Optional</span>
              </label>
              <input
                type="text"
                id="trim"
                name="trim"
                value={formData.trim}
                onChange={handleChange}
                maxLength={50}
                aria-invalid={!!validationErrors.trim}
                aria-describedby={validationErrors.trim ? 'sell-trim-error' : undefined}
                className={fieldClass('trim')}
                placeholder="e.g. XLE, Sport, Limited"
              />
              {validationErrors.trim && <p id="sell-trim-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.trim}</p>}
            </div>
            <div>
              <label htmlFor="mileage" className="flex items-center gap-2 text-sm font-medium text-[#1A2332] mb-2">
                <i className="ri-dashboard-line text-[#E8B84B]" aria-hidden="true"></i>Mileage
              </label>
              <input type="text" id="mileage" name="mileage" value={formData.mileage} onChange={handleChange} required aria-required="true" aria-invalid={!!validationErrors.mileage} aria-describedby={validationErrors.mileage ? 'sell-mileage-error' : undefined} className={fieldClass('mileage')} placeholder="50,000" />
              {validationErrors.mileage && <p id="sell-mileage-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.mileage}</p>}
            </div>
            <div>
              <label htmlFor="condition" className="flex items-center gap-2 text-sm font-medium text-[#1A2332] mb-2">
                <i className="ri-star-line text-[#E8B84B]" aria-hidden="true"></i>Condition
              </label>
              <select id="condition" name="condition" value={formData.condition} onChange={handleChange} required aria-required="true" aria-invalid={!!validationErrors.condition} aria-describedby={validationErrors.condition ? 'sell-condition-error' : undefined} className={fieldClass('condition')}>
                <option value="">Select condition</option>
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
              </select>
              {validationErrors.condition && <p id="sell-condition-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.condition}</p>}
            </div>
          </div>
        </div>

        <div>
          <label htmlFor="message" className="flex items-center gap-2 text-sm font-medium text-[#1A2332] mb-2">
            <i className="ri-message-3-line text-[#E8B84B]" aria-hidden="true"></i>Additional Details
          </label>
          <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows={4} maxLength={500} aria-describedby={`sell-message-count${validationErrors.message ? ' sell-message-error' : ''}`} className={fieldClass('message')} placeholder="Tell us about any special features, recent maintenance, or other details..." />
          <div className="flex justify-between mt-1">
            {validationErrors.message ? <p id="sell-message-error" role="alert" className="text-red-500 text-xs">{validationErrors.message}</p> : <span />}
            <p id="sell-message-count" className="text-xs text-[#4A5568]" aria-live="polite">{formData.message.length}/500</p>
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-[#1A2332] text-white py-4 rounded-lg font-semibold text-lg hover:bg-[#2C3E55] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B] focus-visible:ring-offset-2"
        >
          {isSubmitting ? (
            <><i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>Submitting...</>
          ) : (
            <><i className="ri-send-plane-fill" aria-hidden="true"></i>Get My Cash Offer</>
          )}
        </button>
      </form>
    </div>
  );
}
